import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobToken } from '@/utils/tokenCombatState'
import { VTTCanvas } from '@/components/VTTCanvas'
import { LandingPage } from '@/components/LandingPage'
import { RightSidebar } from '@/components/RightSidebar'
import { TopBar } from '@/components/TopBar'
import { CanvasOverlay } from '@/components/CanvasOverlay'
import { PropertiesHUD } from '@/components/PropertiesHUD'
import { useIsMobile } from '@/hooks/useIsMobile'
import { CharacterSheetModal } from '@/components/CharacterSheetModal'
import { NpcTokenModal } from '@/components/NpcTokenModal'
import { BlankGrid } from '@/components/BlankGrid'
import { HeroLoadingScreen } from '@/components/HeroLoadingScreen'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useCharacterData } from '@/hooks/useCharacterData'
import { useSessionMeta } from '@/hooks/useSessionMeta'
import { socketService } from '@/services/socket'
import {
  cloneCharacter,
  updateCharacterHp,
  updateCharacterClassification,
  setCharacterServiceAuthExpiredHandler,
} from '@/services/characterService'
import type { TokenClassification } from '@/services/characterService'
import { GMHandoffOffer } from '@/components/GMHandoffOffer'
import { InitiativePrompt } from '@/components/InitiativePrompt'
import { useVTTStore } from '@/store'
import {
  CharacterDragProvider,
  CharacterDragGhost,
} from '@/hooks/useCharacterDrag'
import { launchVTTSession } from '@/services/myGamesService'
import { Button } from '@arcanecircle/ui/grimoire/button'

/**
 * Decode JWT payload (without verification - just for display)
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    return null
  }
}

/**
 * Helper to decode and validate a JWT token
 */
function decodeAndReturn(token: string): { token: string; sessionId: string; userId: string; displayName: string; role: string } | null {
  const sessionId = window.location.pathname.split('/session/')[1]?.split('?')[0]
  if (!sessionId) return null

  const payload = decodeJWT(token)
  if (!payload) return null

  return {
    token,
    sessionId,
    userId: payload.userId,
    displayName: payload.displayName,
    role: payload.role,
  }
}

/**
 * Extract JWT token and session ID from URL or sessionStorage
 */
function getAuthFromURL(): { token: string; sessionId: string; userId: string; displayName: string; role: string } | null {
  // First: check URL params (fresh login from platform)
  const params = new URLSearchParams(window.location.search)
  const urlToken = params.get('token')

  if (urlToken) {
    // Persist the token to sessionStorage BEFORE stripping it from
    // the URL — otherwise React StrictMode's double-invocation of
    // this function (it's pure from React's POV, but we mutate the
    // URL here) loses the token between calls 1 and 2 and the user
    // gets bounced to LandingPage. Storing first makes the second
    // call fall through to the sessionStorage branch and recover.
    try {
      sessionStorage.setItem('vtt_token', urlToken)
    } catch {
      // sessionStorage unavailable (incognito, etc) — fine, the
      // memory-resident token in socketService still wires up.
    }

    // Clear token from URL to prevent sharing/bookmarking a URL with a token
    const url = new URL(window.location.href)
    url.searchParams.delete('token')
    window.history.replaceState({}, '', url.toString())

    return decodeAndReturn(urlToken)
  }

  // Second: check sessionStorage (page refresh)
  const storedToken = socketService.getTokenFromStorage()
  if (storedToken) {
    return decodeAndReturn(storedToken)
  }

  return null
}

/**
 * Main Application Component
 * Phase 1-2: Canvas with optional WebSocket sync
 */
function App() {
  // Note: map, tokens, etc. are now accessed within RightSidebar component

  // Check if we have auth from URL (Phase 2 mode)
  const auth = useMemo(() => getAuthFromURL(), [])

  // Live role — seeded from the JWT claim but updated by `role_changed`
  // broadcasts (GM handoff via pass+accept, or take_gm_control rescue
  // when the prior GM is offline). The JWT's role field is whatever it
  // was when the token was issued, so without this state the GM seat
  // can transfer without the right-sidebar GM tools appearing.
  const [liveRole, setLiveRole] = useState<'gm' | 'player' | 'observer'>(
    () => (auth?.role as 'gm' | 'player' | 'observer' | undefined) ?? 'player'
  )

  // Deep-link auto-launch.
  //
  // Someone hitting `${VTT_URL}/session/<id>` directly (e.g. a link
  // from the main platform's dashboard, a Discord post, a calendar
  // invite) lands here with no `?token=…` and no sessionStorage entry,
  // so `getAuthFromURL` returns null and we'd normally render the
  // picker. Instead, if the URL has a sessionId and the user is
  // signed into ac-mvp, mint a JWT via `/api/vtt/launch` (cookie-
  // authed) and redirect to the launchUrl — same end-state as
  // clicking the picker, just one fewer click.
  //
  // Three terminal states from here:
  //   - `running`  → fetch in flight; show a "Connecting…" splash
  //                  instead of bouncing to the picker
  //   - `failed`   → launch endpoint returned 401 / 404 / network
  //                  error; show the picker with whatever error
  //                  copy LandingPage already has for the auth case
  //   - `noop`     → URL had no `/session/<id>` segment, or auth was
  //                  already resolved from `?token=`/sessionStorage
  type AutoLaunchState =
    | { status: 'noop' }
    | { status: 'running' }
    | {
        status: 'failed'
        reason: string
        // HTTP status carried so we can distinguish "you don't have
        // access to this session" (403) from generic transport errors
        // and render a dedicated no-access panel instead of dumping
        // the user onto the picker with no context.
        httpStatus: number
        sessionId: string
      }
  const [autoLaunch, setAutoLaunch] = useState<AutoLaunchState>({
    status: 'noop',
  })
  const autoLaunchAttempted = useRef(false)
  useEffect(() => {
    // Guard: StrictMode double-invokes effects in dev. The redirect we
    // do at the bottom of the success path is permanent, but we still
    // want to avoid firing two parallel /api/vtt/launch POSTs.
    if (autoLaunchAttempted.current) return
    if (auth) return // URL token or sessionStorage already won

    const sessionId = window.location.pathname
      .split('/session/')[1]
      ?.split('?')[0]
    if (!sessionId) return // no deep-link path; LandingPage handles it

    autoLaunchAttempted.current = true
    setAutoLaunch({ status: 'running' })
    launchVTTSession(sessionId).then((result) => {
      if (result.ok) {
        window.location.href = result.url
      } else {
        setAutoLaunch({
          status: 'failed',
          reason: result.error,
          httpStatus: result.status,
          sessionId,
        })
      }
    })
  }, [auth])

  const [connectionError, setConnectionError] = useState<string | null>(null)
  const connectionErrorRef = useRef<((msg: string) => void) | null>(null)

  // Mobile right-sidebar drawer state. Desktop ignores this — the
  // sidebar is permanently docked at >=769px viewports via CSS. On
  // mobile, default closed so the canvas owns the screen on first
  // render; the TopBar hamburger toggles it.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  // Mobile swaps the big bottom-left card for the compact PropertiesHUD.
  const isMobile = useIsMobile()

  // Character sheet modal — driven by store state so it can be opened
  // from the sidebar character rows and the token tooltip. Mounted once
  // at the app root (below) so it overlays the whole shell.
  const characterSheetId = useVTTStore((s) => s.characterSheetId)
  const closeCharacterSheet = useVTTStore((s) => s.closeCharacterSheet)
  // Stats-edit modal (#657) — the token tooltip's "Edit stats" button opens
  // this by characterId; mounted once here alongside the sheet modal above.
  const statsEditCharacterId = useVTTStore((s) => s.statsEditCharacterId)
  const closeStatsEdit = useVTTStore((s) => s.closeStatsEdit)

  // Set ref during render so it's available before any useEffect runs
  connectionErrorRef.current = (msg: string) => setConnectionError(msg)

  // DIAGNOSTIC: catch real page unload + any unhandled error.
  // If the "reload" we're chasing is an actual navigation, beforeunload
  // fires with the URL the browser is heading to. If it's React state
  // wipe / remount, beforeunload does NOT fire.
  useEffect(() => {
    const onUnload = () => {
      console.warn('[App] beforeunload — page is actually navigating away', {
        currentURL: window.location.href,
      })
    }
    const onErr = (e: ErrorEvent) => {
      console.error('[App] window.onerror', e.message, e.filename, e.lineno, e.error)
    }
    const onRej = (e: PromiseRejectionEvent) => {
      console.error('[App] unhandledrejection', e.reason)
    }
    window.addEventListener('beforeunload', onUnload)
    window.addEventListener('error', onErr)
    window.addEventListener('unhandledrejection', onRej)
    return () => {
      window.removeEventListener('beforeunload', onUnload)
      window.removeEventListener('error', onErr)
      window.removeEventListener('unhandledrejection', onRej)
    }
  }, [])

  // Wire onAuthExpired callback (Nakir M5)
  // Must be declared before useWebSocket to ensure effect runs first
  useEffect(() => {
    const handler = () => {
      console.error('[App] Session expired — re-authentication required')
      connectionErrorRef.current?.('Session expired. Please re-join from the platform.')
    }
    socketService.onAuthExpired = handler
    // Route REST 401s through the same handler so character-data
    // fetches stop showing stale cached data after JWT expiry.
    setCharacterServiceAuthExpiredHandler(handler)
    return () => {
      socketService.onAuthExpired = null
      setCharacterServiceAuthExpiredHandler(null)
    }
  }, [])

  // Forward-declared ref for the per-character refetch path. useWebSocket
  // is invoked before useCharacterData (it gates `enabled` on
  // wsStatus.connected), so we hand the hook a stable callback that
  // dereferences the ref. App fills the ref after useCharacterData
  // returns.
  const refetchCharacterRef = useRef<
    | ((characterId: string, opts?: { invalidate?: boolean }) => Promise<unknown>)
    | null
  >(null)

  const handleCharacterNeedsRefresh = useCallback(
    (characterId: string, previousCharacterId: string | null) => {
      refetchCharacterRef.current?.(characterId, { invalidate: true })
      // When a token re-links, also drop the old characterId from the
      // module-scoped cache so anything still keyed on it stops serving
      // stale data.
      if (previousCharacterId && previousCharacterId !== characterId) {
        refetchCharacterRef.current?.(previousCharacterId, { invalidate: true })
      }
    },
    []
  )

  const handleRoleChanged = useCallback((newRole: 'gm' | 'player') => {
    setLiveRole(newRole)
  }, [])

  // Initialize WebSocket if we have auth
  const wsStatus = useWebSocket(
    auth
      ? {
          token: auth.token,
          sessionId: auth.sessionId,
          currentUserId: auth.userId,
          role: liveRole,
          onConnected: () => console.log('WebSocket connected!'),
          onDisconnected: () => console.log('WebSocket disconnected'),
          onError: (error) => console.error('WebSocket error:', error),
          onRoleChanged: handleRoleChanged,
          onCharacterNeedsRefresh: handleCharacterNeedsRefresh,
        }
      : { token: '', sessionId: '', currentUserId: '' } // Dummy values, hook will handle gracefully
  )

  // Fetch character data for session — drives token tooltips, the
  // Characters tab in the right sidebar, and the drag-to-spawn party
  // roster.
  const {
    getCharacter,
    characters,
    loading: charactersLoading,
    error: charactersError,
    refetchCharacter,
  } = useCharacterData({
    sessionId: auth?.sessionId || '',
    token: auth?.token || '',
    enabled: !!auth && wsStatus.connected,
  })

  // Wire the forward-declared ref so the WebSocket callback can drive
  // per-character refetches.
  useEffect(() => {
    refetchCharacterRef.current = refetchCharacter
    return () => {
      refetchCharacterRef.current = null
    }
  }, [refetchCharacter])

  // Hydrate a character whose HP the canvas needs but couldn't resolve yet
  // (#161 follow-up): the HP pill draws from getCharacter, but on a fresh load
  // a token can paint before the session-roster fetch has merged its linked
  // character into state — so the bar stayed hidden until the player opened the
  // sheet (which fetched that one character). VTTCanvas now calls this on a
  // draw-time miss; we refetch and merge into `characters`, which gives
  // getCharacter a new identity and re-runs the token-draw effect so the pill
  // appears on its own. Don't invalidate — if the roster cached partial data we
  // still want a fresh single fetch, but a plain merge is enough.
  const handleCharacterHydrate = useCallback(
    (characterId: string) => {
      void refetchCharacter(characterId, { invalidate: true })
    },
    [refetchCharacter]
  )

  // Fetch session metadata — game title, system, sessionNumber, GM
  // name, expected-player roster (with hasCharacter flags). Drives
  // the TopBar breadcrumb and the chat-tab roster.
  const { meta: sessionMeta } = useSessionMeta({
    sessionId: auth?.sessionId || '',
    token: auth?.token || '',
    enabled: !!auth,
  })

  // Whether a real active map exists. Subscribed here (not inside the
  // canvas) so that when it flips to false the canvas is unmounted from
  // the parent and we render <BlankGrid> instead — see the canvas-area
  // gate below. Must run before any early return.
  const hasActiveMap = useVTTStore(
    (s) => s.activeMapId != null && s.maps.some((m) => m.id === s.activeMapId),
  )

  // Spawn a token from the Characters tab (GM drag-and-drop onto the
  // canvas). Server resolves the image from the linked character and
  // enforces GM-only.
  const handleSpawnToken = useCallback(
    (payload: { mapId: string; name: string; x: number; y: number; characterId?: string; imageUrl?: string; ownerUserId?: string }) => {
      if (wsStatus.connected) {
        socketService.createToken(payload)
      }
    },
    [wsStatus.connected]
  )

  // Delete a token (from the right-click context menu). Server enforces
  // GM-only; the broadcast `token_deleted` event removes it from every
  // participant's store, including ours.
  const handleTokenDelete = useCallback(
    (tokenId: string) => {
      if (wsStatus.connected) {
        socketService.deleteToken(tokenId)
      }
    },
    [wsStatus.connected]
  )

  // Rename a token (from the bottom-left HUD card). Optimistically
  // update the local store so the change shows immediately, then emit
  // `token_update` over the socket; the server validates owner/GM and
  // broadcasts `token_updated`, which `useWebSocket` applies via the store.
  const handleTokenRename = useCallback(
    (tokenId: string, newName: string) => {
      const { tokens, setTokens } = useVTTStore.getState()
      setTokens(tokens.map((t) => (t.id === tokenId ? { ...t, name: newName } : t)))
      if (wsStatus.connected) {
        socketService.updateToken(tokenId, { name: newName })
      }
    },
    [wsStatus.connected]
  )

  // Owner-or-GM: torch toggle + light radius from the bottom-left HUD card.
  // Optimistic update + token_update; server gates to owner-or-GM.
  const handleTokenLightChange = useCallback(
    (
      tokenId: string,
      updates: { lightRadius?: number | null; torchActive?: boolean },
    ) => {
      const { tokens, setTokens } = useVTTStore.getState()
      setTokens(tokens.map((t) => (t.id === tokenId ? { ...t, ...updates } : t)))
      if (wsStatus.connected) {
        socketService.updateToken(tokenId, updates)
      }
    },
    [wsStatus.connected]
  )

  // Owner-or-GM toggle for whether other players may see a token's HP bar
  // (from the bottom-left HUD card). Same optimistic-update + socket path as
  // rename; the server gates it to the token's owner or the GM.
  const handleTokenHpBarVisibilityChange = useCallback(
    (tokenId: string, visibleToPlayers: boolean) => {
      const { tokens, setTokens } = useVTTStore.getState()
      setTokens(
        tokens.map((t) =>
          t.id === tokenId ? { ...t, hpBarVisibleToPlayers: visibleToPlayers } : t
        )
      )
      if (wsStatus.connected) {
        socketService.updateToken(tokenId, { hpBarVisibleToPlayers: visibleToPlayers })
      }
    },
    [wsStatus.connected]
  )

  // Edit a linked character's current HP on the sheet (issue #135). Write to the
  // platform, then signal the session so every client refetches the new HP.
  const handleHpEdit = useCallback(
    async (
      characterId: string,
      change: { set: number } | { delta: number },
      tokenId?: string
    ) => {
      // Mob token path: write to token.instanceStats, never the shared sheet.
      if (tokenId && wsStatus.connected) {
        const token = useVTTStore.getState().tokens.find((t) => t.id === tokenId)
        if (token && isMobToken(token)) {
          const stats = token.instanceStats!
          const max = stats.hp.max
          let newCurrent: number
          if ('set' in change) {
            newCurrent = Math.max(0, Math.min(max, change.set))
          } else {
            newCurrent = Math.max(0, Math.min(max, stats.hp.current + change.delta))
          }
          socketService.emitTokenInstanceStatsUpdate(tokenId, {
            hp: { current: newCurrent, max, temp: stats.hp.temp },
            statuses: stats.statuses,
          })
          return
        }
      }
      // Non-mob path: write through the platform API (character sheet).
      if (!auth?.token) return
      const res = await updateCharacterHp(characterId, change, auth.token)
      if (res && wsStatus.connected) {
        socketService.emitCharacterHpChanged(characterId)
      }
    },
    [auth?.token, wsStatus.connected]
  )

  // Re-classify a token's linked character (VTT #163) — from the right-click
  // menu's segmented toggle. Same write-then-signal shape as handleHpEdit:
  // PATCH the character on the platform, then emit so every client refetches
  // and the bank panel re-buckets + the enemy outline redraws. GM-only (the
  // menu gates the control and the endpoint enforces edit permission).
  const handleClassificationChange = useCallback(
    async (characterId: string, classification: TokenClassification) => {
      if (!auth?.token) return
      const ok = await updateCharacterClassification(
        characterId,
        classification,
        auth.token
      )
      if (ok && wsStatus.connected) {
        socketService.emitCharacterClassificationChanged(characterId)
      }
    },
    [auth?.token, wsStatus.connected]
  )

  // Bulk delete: fired by the canvas Delete/Backspace handler when
  // more than one token is selected. Routes through the
  // `tokens_delete` socket event so we don't hit the per-event
  // `token_delete` rate limit (5/sec) on selections of six or more.
  const handleTokensDelete = useCallback(
    (tokenIds: string[]) => {
      if (!wsStatus.connected) return
      if (tokenIds.length === 0) return
      if (tokenIds.length === 1) {
        socketService.deleteToken(tokenIds[0])
        return
      }
      socketService.deleteTokens(tokenIds)
    },
    [wsStatus.connected]
  )

  // Disconnect a token from a shared mob: clone the character on the
  // platform, then repoint the token to the clone via socket. Afterwards
  // this token is its own unique stat block — it no longer shares HP/state
  // with the original mob row.
  const handleTokenDisconnect = useCallback(
    async (tokenId: string) => {
      if (!wsStatus.connected || !auth) return

      const token = useVTTStore.getState().tokens.find((t) => t.id === tokenId)
      if (!token?.characterId) return

      const cloneId = await cloneCharacter(token.characterId, auth.token)
      if (!cloneId) {
        console.error('[App] handleTokenDisconnect: clone failed for', token.characterId)
        return
      }

      socketService.updateToken(tokenId, { characterId: cloneId })
    },
    [wsStatus.connected, auth]
  )

  // Handle token movement (emit to WebSocket if connected)
  // Phase 3c: Added instant parameter for keyboard moves
  const handleTokenMove = useCallback(
    (tokenId: string, x: number, y: number, waypoints?: Array<{ x: number; y: number }>, instant?: boolean) => {
      if (!wsStatus.connected) return
      // Movement is locked until a GM is present. The server rejects these
      // too, but short-circuiting here avoids a doomed round-trip and the
      // resulting snap-back. A GM (or GM-control holder) is exempt.
      const gmHere =
        liveRole === 'gm' || wsStatus.participants.some((p) => p.role === 'gm')
      if (!gmHere) return
      socketService.moveToken(tokenId, { x, y }, waypoints, instant)
    },
    [wsStatus.connected, wsStatus.participants, liveRole]
  )

  // Handle barrier creation (emit to WebSocket if connected)
  const handleBarrierCreate = useCallback(
    (mapId: string, points: Array<{ x: number; y: number }>, closed?: boolean) => {
      if (wsStatus.connected) {
        socketService.createBarrier(mapId, points, closed)
      }
    },
    [wsStatus.connected]
  )

  // Handle barrier deletion (emit to WebSocket if connected)
  const handleBarrierDelete = useCallback(
    (barrierId: string) => {
      if (wsStatus.connected) {
        socketService.deleteBarrier(barrierId)
      }
    },
    [wsStatus.connected]
  )

  // Handle map configuration update (API call)
  const handleMapUpdate = useCallback(
    async (mapId: string, updates: any) => {
      if (!auth) return

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'

      try {
        const response = await fetch(`${apiUrl}/api/maps/${mapId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`,
          },
          body: JSON.stringify(updates),
        })

        if (!response.ok) {
          throw new Error('Failed to update map')
        }

        const updatedMap = await response.json()
        console.log('[App] Map updated:', updatedMap)

        // The WebSocket will handle broadcasting the update to all clients
        // via the map_updated event
      } catch (error) {
        console.error('[App] Error updating map:', error)
        alert('Failed to update map. Please try again.')
      }
    },
    [auth]
  )

  // Handle map deletion (GM only — server enforces). The map_deleted
  // WebSocket event drops the map from every client's store; here we
  // only fire the REST call and, if the deleted map was the active one,
  // switch the GM to a surviving map so they aren't left on a blank
  // table. Snapshot store state BEFORE the call so the auto-switch is
  // immune to the map_deleted event's arrival timing.
  const handleMapDelete = useCallback(
    async (mapId: string) => {
      if (!auth) return

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
      const { maps: mapsBefore, activeMapId: activeBefore } =
        useVTTStore.getState()

      try {
        const response = await fetch(`${apiUrl}/api/maps/${mapId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${auth.token}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to delete map')
        }

        console.log('[App] Map deleted:', mapId)

        if (activeBefore === mapId) {
          const next = mapsBefore.find((m) => m.id !== mapId)
          if (next) {
            useVTTStore.getState().switchMap(next.id)
            try {
              socketService.emitMapSwitch(next.id)
            } catch (err) {
              console.error('[App] map_switch after delete failed', err)
            }
          }
        }
      } catch (error) {
        console.error('[App] Error deleting map:', error)
        alert('Failed to delete map. Please try again.')
      }
    },
    [auth]
  )

  // Handle map creation (API call with optional image upload)
  const handleMapCreate = useCallback(
    async (mapData: any, imageFile: File | null) => {
      if (!auth) return

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'

      try {
        let backgroundUrl = null

        // Upload image if provided
        if (imageFile) {
          const formData = new FormData()
          formData.append('image', imageFile)

          // Server's `requireGM` middleware verifies JWT and binds the
          // upload to the caller's session — no need to (and we
          // shouldn't) pass sessionId as a query param.
          const uploadResponse = await fetch(`${apiUrl}/api/maps/upload`, {
            method: 'POST',
            headers: {
              // No Content-Type — the browser sets the multipart boundary for
              // FormData. The upload route is behind requireAuth/requireGM, so
              // the Bearer token is required (this was the missing piece).
              Authorization: `Bearer ${auth.token}`,
            },
            body: formData,
          })

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.json().catch(() => ({ error: 'Unknown error' }))
            throw new Error(errorData.error || `Failed to upload image (${uploadResponse.status})`)
          }

          const uploadResult = await uploadResponse.json()
          backgroundUrl = uploadResult.url
        }

        // Create map
        const response = await fetch(`${apiUrl}/api/sessions/${auth.sessionId}/maps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`,
          },
          body: JSON.stringify({
            ...mapData,
            backgroundUrl,
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to create map')
        }

        const newMap = await response.json()
        console.log('[App] Map created:', newMap)

        // The WebSocket will handle broadcasting the new map to all clients
        // via the map_created event
      } catch (error) {
        console.error('[App] Error creating map:', error)
        alert('Failed to create map. Please try again.')
        throw error
      }
    },
    [auth]
  )

  // Handle fog reveal (emit to WebSocket if connected)
  const handleFogReveal = useCallback(
    (mapId: string, cells: Array<{ x: number; y: number }>) => {
      if (wsStatus.connected) {
        socketService.revealFog(mapId, cells)
      }
    },
    [wsStatus.connected]
  )

  // Handle fog hide (emit to WebSocket if connected)
  const handleFogHide = useCallback(
    (mapId: string, cells: Array<{ x: number; y: number }>) => {
      if (wsStatus.connected) {
        socketService.hideFog(mapId, cells)
      }
    },
    [wsStatus.connected]
  )

  // Ctrl+C / Ctrl+V — token copy-paste. GM-only.
  // Ctrl+C with a single selected token copies it to the clipboard.
  // Ctrl+V pastes a duplicate onto the map.
  // Guard fires only when no input/textarea/select is focused to avoid
  // swallowing normal text editing shortcuts.
  const { tokenClipboard, setTokenClipboard, selectedTokenIds } = useVTTStore()
  useEffect(() => {
    if (liveRole !== 'gm' || !wsStatus.connected) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) return

      if (e.key === 'c' || e.key === 'C') {
        // Only copy when exactly one token is selected.
        if (selectedTokenIds.length !== 1) return
        setTokenClipboard(selectedTokenIds[0])
        e.preventDefault()
        return
      }

      if (e.key === 'v' || e.key === 'V') {
        if (!tokenClipboard) return
        const store = useVTTStore.getState()
        const token = store.tokens.find((t) => t.id === tokenClipboard)
        if (!token) { setTokenClipboard(null); return }

        // Determine isMob from the token's instanceStats.
        const mob = isMobToken(token)
        const seedHp = mob && token.instanceStats
          ? { current: token.instanceStats.hp.max, max: token.instanceStats.hp.max, temp: 0 }
          : undefined

        // If the token isn't a mob but we want to paste the statblock HP, we
        // need to get it from the character. For now emit without seedHp for
        // non-mob tokens (server defaults to 10/10 which is fine since non-mob
        // pastes don't use instanceStats anyway).
        socketService.emitTokenCopyPaste({
          sourceTokenId: tokenClipboard,
          isMob: mob,
          seedHp,
        })
        e.preventDefault()
        return
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [liveRole, wsStatus.connected, selectedTokenIds, tokenClipboard, setTokenClipboard])

  const isPhase2 = auth !== null

  // Mid-deep-link splash. Render between "URL says /session/<id>" and
  // "the cookie-authed launch endpoint hands us back a JWT" so the
  // user doesn't see the picker flash. Held on the same dark shell as
  // the rest of the app so the transition into the session canvas is
  // visually continuous.
  if (!isPhase2 && autoLaunch.status === 'running') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'hsl(var(--ac-dark-primary))',
          color: 'hsl(var(--ac-text-muted))',
          fontSize: '0.875rem',
          letterSpacing: '0.08em',
        }}
      >
        Opening tabletop…
      </div>
    )
  }

  // Deep-link to a session the user can't access. The launch endpoint
  // already validates membership server-side (GM or approved booking),
  // so a 403/404 here means the user is not on the roster — show a
  // dedicated panel instead of dumping them on the picker with no
  // context. Other failures (401, 5xx, network) fall through to the
  // picker, which has its own auth/network copy.
  if (
    !isPhase2 &&
    autoLaunch.status === 'failed' &&
    (autoLaunch.httpStatus === 403 || autoLaunch.httpStatus === 404)
  ) {
    return <NoSessionAccessPanel reason={autoLaunch.reason} />
  }

  // Show landing page for unauthenticated users or auth failures
  if (!isPhase2 || wsStatus.authFailed || connectionError) {
    return <LandingPage />
  }

  // Authenticated, but the tabletop hasn't hydrated yet. `wsStatus.connected`
  // flips true only when the server's `session_state` arrives, so until then
  // we hold on a hero-image loading screen instead of mounting the shell over
  // an empty store (which is what produced the old default-map flash).
  if (!wsStatus.connected) {
    return (
      <HeroLoadingScreen
        heroImage={sessionMeta?.heroImage}
        gameImage={sessionMeta?.gameImage}
        gameTitle={sessionMeta?.gameTitle}
      />
    )
  }

  const role = liveRole
  const isGM = role === 'gm'

  // Whether a GM (or a player holding GM control) is connected. Token
  // movement is locked when no GM is present — enforced authoritatively on
  // the server; mirrored here to disable local moves and surface the banner.
  // If *we* are the GM, a GM is trivially present (also avoids a banner
  // flash before the first participants_updated arrives).
  const gmPresent =
    isGM || wsStatus.participants.some((p) => p.role === 'gm')

  // Mobile drawer state for the right sidebar. Always closed on first
  // render so the canvas owns the viewport when the page first lands;
  // user opens it from the TopBar hamburger. Desktop ignores this via
  // CSS — the sidebar is always docked at >=769px.
  const sidebarOpen = mobileSidebarOpen
  const closeSidebar = () => setMobileSidebarOpen(false)

  return (
    <CharacterDragProvider>
      <div
        className={`app-shell${sidebarOpen ? ' is-sidebar-open' : ''}`}
      >
        <TopBar
          displayName={auth.displayName}
          role={role}
          connected={wsStatus.connected}
          participantCount={wsStatus.participants.length}
          participantMax={sessionMeta?.expectedPlayers.length}
          gameTitle={sessionMeta?.gameTitle}
          sessionNumber={sessionMeta?.sessionNumber ?? undefined}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setMobileSidebarOpen((v) => !v)}
        />

        {/* GM-absent banner. Token movement is locked until a GM joins;
            this flag makes that explicit rather than leaving players to
            wonder why their tokens snap back. Hidden for the GM (and once
            any GM is present). */}
        {wsStatus.connected && !gmPresent && (
          <div
            role="status"
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'hsl(var(--ac-gm-gold))',
              background: 'hsl(var(--ac-gm-gold) / 0.12)',
              borderBottom: '1px solid hsl(var(--ac-gm-gold) / 0.35)',
              textAlign: 'center',
            }}
          >
            <span aria-hidden style={{ fontSize: '0.9rem', lineHeight: 1 }}>
              ⚠
            </span>
            The GM is not present — token movement is locked until they join.
          </div>
        )}

        <div className="app-body">
        <div className="canvas-area">
          {/* Only mount the heavy PIXI canvas when there's a real active
              map. VTTCanvas has an `if (!map) return` above ~30 hooks, so
              flipping the active map to null in a *mounted* instance would
              change its hook count and tear the tree down. Gating the mount
              here means it unmounts cleanly (the WS socket lives at the App
              level, so it's unaffected) and we show a blank grid instead. */}
          {hasActiveMap ? (
            <VTTCanvas
              onTokenMove={handleTokenMove}
              onBarrierCreate={handleBarrierCreate}
              onBarrierDelete={handleBarrierDelete}
              onFogReveal={handleFogReveal}
              onFogHide={handleFogHide}
              currentUserId={auth?.userId}
              userRole={role}
              getCharacterData={getCharacter}
              onCharacterHydrate={handleCharacterHydrate}
              onHpEdit={handleHpEdit}
              onSpawnToken={handleSpawnToken}
              onTokensDelete={handleTokensDelete}
              onTokenDisconnect={handleTokenDisconnect}
              onClassificationChange={handleClassificationChange}
            />
          ) : (
            <BlankGrid isGM={isGM} />
          )}
          <CanvasOverlay
            getCharacter={getCharacter}
            userRole={role}
            currentUserId={auth?.userId}
            onRename={handleTokenRename}
            onDelete={handleTokenDelete}
            onLightChange={handleTokenLightChange}
            onHpBarVisibilityChange={handleTokenHpBarVisibilityChange}
            onHpEdit={handleHpEdit}
            showSelectedPanel={!isMobile}
          />
          {/* Mobile: compact HUD owns the selected token OR light. Desktop:
              the CanvasOverlay card above handles both. */}
          {isMobile && (
            <PropertiesHUD
              userRole={role}
              currentUserId={auth?.userId}
              onDelete={handleTokenDelete}
              onLightChange={handleTokenLightChange}
            />
          )}
        </div>

        {/* Mobile drawer backdrop — only rendered when the drawer is
            open. Hidden on desktop via CSS (the sidebar is docked).
            Tap dismisses. */}
        {sidebarOpen && (
          <button
            type="button"
            className="vtt-mobile-backdrop"
            aria-label="Close sidebar"
            onClick={closeSidebar}
          />
        )}

        <RightSidebar
          isGM={isGM}
          currentUserId={auth.userId}
          displayName={auth.displayName}
          sessionId={auth.sessionId}
          authToken={auth.token}
          participants={wsStatus.participants}
          expectedPlayers={sessionMeta?.expectedPlayers}
          onMapUpdate={handleMapUpdate}
          onMapCreate={handleMapCreate}
          onMapDelete={handleMapDelete}
          getCharacter={getCharacter}
          characters={characters}
          charactersLoading={charactersLoading}
          charactersError={charactersError}
          isMobileOpen={sidebarOpen}
          onMobileClose={closeSidebar}
        />
      </div>
      <CharacterDragGhost />
      {characterSheetId && (
        <CharacterSheetModal
          characterId={characterSheetId}
          authToken={auth.token}
          onClose={closeCharacterSheet}
        />
      )}
      {statsEditCharacterId && (
        <NpcTokenModal
          authToken={auth.token}
          sessionId={auth.sessionId}
          onClose={closeStatsEdit}
          editTarget={{
            characterId: statsEditCharacterId,
            name: getCharacter(statsEditCharacterId)?.name ?? 'Token',
          }}
        />
      )}
      <GMHandoffOffer />
      <InitiativePrompt currentUserId={auth?.userId} isGM={isGM} />
    </div>
    </CharacterDragProvider>
  )
}

export default App

/**
 * Rendered when a deep-linked `/session/<id>` URL hits the cookie-
 * authed launch endpoint and gets a 403 / 404 back — i.e. the user
 * is signed in to ac-mvp but is not on this session's roster (not
 * the GM, no approved booking, or the session does not exist).
 *
 * Distinct from the picker's tier gate: the user is allowed on the
 * VTT, just not in THIS room.
 */
function NoSessionAccessPanel({ reason }: { reason: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'hsl(var(--ac-surface-0))',
        color: 'hsl(var(--ac-text-light))',
        padding: '1.5rem',
      }}
    >
      <section
        className="ac-card-hero"
        style={{
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          textAlign: 'center',
          maxWidth: 480,
        }}
      >
        <div
          className="font-script"
          style={{
            fontSize: '0.625rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'hsl(var(--ac-warning))',
          }}
        >
          Access denied
        </div>
        <h2
          className="font-serif-ac"
          style={{ fontSize: '1.5rem', lineHeight: 1.15, margin: 0 }}
        >
          You're not on this session's roster
        </h2>
        <p
          style={{
            fontSize: '0.875rem',
            color: 'hsl(var(--ac-text-muted))',
            lineHeight: 1.6,
          }}
        >
          Only the GM and approved players can join this session. If you
          should have access, ask your GM to confirm the booking on
          Arcane Circle.
        </p>
        <div
          className="ac-help"
          style={{
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
          }}
        >
          {reason}
        </div>
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'center',
            marginTop: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          <Button asChild size="sm">
            <a href="/" style={{ textDecoration: 'none' }}>
              Back to your games
            </a>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a
              href="https://arcanecircle.games"
              style={{ textDecoration: 'none' }}
            >
              Arcane Circle
            </a>
          </Button>
        </div>
      </section>
    </div>
  )
}
